import React from 'react';
import Image from 'next/image';
import imageUrlBuilder from '@sanity/image-url';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import { merge, cloneDeep } from 'lodash-es';
import { getImageDimensions } from '@sanity/asset-utils';
import { prodClient } from './lib/client';
import { sanityImageUrl } from '@/lib/utils';
import greenLeft from '@/public/global/green-highlight-left.svg';
import greenRight from '@/public/global/green-highlight-right.svg';
import pinkLeft from '@/public/manifesto/pink-highlight-left.svg';
import pinkRight from '@/public/manifesto/pink-highlight-right.svg';
import amazon from '@/public/icons/amazon.svg';

const sanityImageBuilder = imageUrlBuilder(prodClient);

export const richTextComponent: PortableTextComponents = {
  marks: {
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    orangeFlare: ({ children }) => (
      <span className="orange-shadow">{children}</span>
    ),
    lightGreenText: ({ children }) => (
      <span className="text-[#D9F9B9] text-[1.75rem] font-geistmono uppercase !not-italic font-medium">
        {children}
      </span>
    ),
    darkGreenText: ({ children }) => (
      <span className="text-[#052920] text-[1.75rem] font-geistmono uppercase !not-italic font-medium">
        {children}
      </span>
    ),
    orangeBackground: ({ children }) => (
      <span className="orange-shadow-bg text-white">{children}</span>
    ),
    greenBackground: ({ children }) => (
      <div className="relative z-[-1] inline-block">
        <div className="absolute left-[2rem] top-[60%] -translate-y-1/2 h-[80%] w-[calc(100%-3.5rem)] bg-[#D9F9B9]"></div>
        <Image
          src={greenLeft}
          alt="green left"
          className="!m-0 absolute right-[calc(100%-2rem)] top-[60%] -translate-y-1/2 !h-[80%] !w-auto pointer-events-none"
        />
        <Image
          src={greenRight}
          alt="green right"
          className="!m-0 absolute left-[calc(100%-1.5rem)] top-[60%] -translate-y-1/2 !h-[80%] !w-auto pointer-events-none"
        />
        <span className="font-ivy !font-[300] relative z-[1]">{children}</span>
      </div>
    ),
    pinkBackgroundPixel: ({ children }) => (
      <div className="relative inline-block px-[.5rem] w-max">
        <div className="opacity-40">
          <div className="absolute left-[2rem] top-[60%] -translate-y-1/2 h-[80%] w-[calc(100%-3.5rem)] bg-[#F97474]"></div>
          <Image
            src={pinkLeft}
            alt="pink left"
            className="!m-0 absolute right-[calc(100%-2rem)] top-[60%] -translate-y-1/2 !h-[80%] !w-auto pointer-events-none"
          />
          <Image
            src={pinkRight}
            alt="pink right"
            className="!m-0 absolute left-[calc(100%-1.5rem)] top-[60%] -translate-y-1/2 !h-[80%] !w-auto pointer-events-none"
          />
        </div>
        <span className="font-ivy !font-[300] relative z-[1]">{children}</span>
      </div>
    ),
    greenBackgroundUnderline: ({ children }) => (
      <div className="inline-block relative">
        <div className="absolute left-1/2 -translate-x-1/2 h-[80%] w-[calc(100%+2rem)] top-1/2 -translate-y-[44%] bg-[#D9F9B9]/10 border-b border-b-[#D9F9B9]/70" />
        <span className="relative text-[#D9F9B9] font-ivy font-300">
          {children}
        </span>
      </div>
    ),
    redBackgroundUnderline: ({ children }) => (
      <div className="inline-block relative">
        <div className="absolute left-0 h-[80%] w-full top-1/2 -translate-y-[44%] bg-[#F97474]/10 border-b border-b-[#F97474]" />
        <span className="relative text-[#F97474] font-ivy font-300">
          {children}
        </span>
      </div>
    ),
    greyBackgroundUnderline: ({ children }) => (
      <div className="inline-block relative">
        <div className="absolute left-0 h-[80%] w-full top-1/2 -translate-y-[44%] bg-[#1E3739]/5 border-b border-b-[#1E3739]/70" />
        <span className="relative font-ivy font-300">{children}</span>
      </div>
    ),
    greenBackgroundLeftBorder: ({ children }) => (
      <div className="inline-block relative border-l border-[#D9F9B9] bg-[#D9F9B9]/10 px-[3rem] py-[1.5rem]">
        {/*<div className="absolute left-0 h-[80%] w-full top-1/2 -translate-y-[44%] bg-[#D9F9B9]/10 border-b border-b-[#D9F9B9]/70" />*/}
        <span className="relative text-[#D9F9B9] text-[2.25rem] font-geist font-300">
          {children}
        </span>
      </div>
    ),
    pinkBackground: ({ children }) => (
      <div className="relative inline-block px-[.5rem]">
        <div className="absolute left-1/2 top-[60%] -translate-x-1/2 -translate-y-1/2 h-[85%] w-full bg-[#F97474]/40"></div>
        <span className="font-ivy !font-[300] relative z-[1]">{children}</span>
      </div>
    ),
    underline: ({ children }) => <u className="underline">{children}</u>,
    em: ({ children }) => <em className="pr-[.125em]">{children}</em>,
    inlineImage: ({ value }) => {
      const url = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : '';
      const dimensions = value?.image?.asset?._ref
        ? getImageDimensions(value?.image?.asset?._ref)
        : {
            width: 0,
            height: 0,
            aspectRatio: 0,
          };
      return (
        <Image
          className="inline-icon inline-block mx-2"
          src={{
            src: url,
            width: dimensions.width,
            height: dimensions.height,
          }}
          alt="Icon image"
          quality={100}
        />
      );
    },
    inlineCornerChip: ({ value, children }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || '';

      return (
        <span className="rt-inlineCornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <span className="font-geistmono text-black leading-none text-[2rem] translate-y-[.125rem]">
            {text}
          </span>
        </span>
      );
    },
  },
  block: {
    h1: ({ children }) => <h1 className="">{children}</h1>,
    h2: ({ children }) => <h2 className="">{children}</h2>,
    h3: ({ children }) => <h3 className="">{children}</h3>,
    h4: ({ children }) => <h4 className="">{children}</h4>,
    h5: ({ children }) => <h5 className="">{children}</h5>,
    h6: ({ children }) => <h6 className="">{children}</h6>,
    normal: ({ children }) => <p className="p-normal">{children}</p>,
  },
  types: {
    image: ({ value }) => {
      return (
        <Image
          className="rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(value).url(),
            width: getImageDimensions(value)?.width,
            height: getImageDimensions(value)?.height,
          }}
          alt={value?.alt || ''}
        />
      );
    },
    solidLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[4rem]';

      return (
        <hr
          className={`w-full border-t border-white border-opacity-20 ${marginClass}`}
        />
      );
    },
    dashedLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[5rem]';

      return (
        <hr
          className={`w-full border-t border-dashed border-white border-opacity-20 ${marginClass}`}
        />
      );
    },
    cornerList: ({ value }) => {
      return (
        <ul className="cornerList list-none p-0 mb-[1.25em] flex flex-col items-start gap-[1rem]">
          {value?.items?.map((item: any, index: number) => {
            return (
              <li key={index} className="flex items-center m-0 p-0 gap-[1rem]">
                <div className="cornerListCorner bg-[#112628] flex-shrink-0 w-[1.5rem] h-[1.5rem] [clip-path:polygon(0_0,calc(100%-.75rem)_0,100%_.75rem,100%_100%,0_100%)]" />
                <div className="richtext richtext-start prose-p:leading-[1.1em]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      );
    },
    cornerChip: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || '';

      return (
        <div className="rt-cornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] prose-p:text-black bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <p className="font-geistmono mb-0 leading-none text-[2rem] translate-y-[.125rem]">
            {text}
          </p>
        </div>
      );
    },
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;

      return (
        <ol
          className="numberedList list-none p-0 mb-[1.25rem] flex flex-col items-start gap-[1rem]"
          start={startNumber}
        >
          {items.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center gap-[.625rem] m-0 p-0"
              >
                <span className="text-[#D9F9B9] font-geist flex-shrink-0 min-w-[2rem]">
                  {startNumber + index}.
                </span>

                <div className="richtext richtext-start">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    amazonButton: ({ value }) => {
      const title = value?.title || '';
      const url = value?.url || '';
      const openInNewTab = value?.openInNewTab || false;

      return (
        <a
          href={url}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className="w-full inline-flex items-center justify-center no-underline mb-[2rem] min-h-[7rem] border border-white/25 bg-white/[.08] hover:bg-white/[.12] transition-colors px-[2rem]py-[1rem] gap-[2rem]"
        >
          <span className="text-white text-[2.25rem] font-geist font-medium">
            {title}
          </span>
          <div className="h-[4rem] w-px bg-white" />
          <Image
            className="!w-[8.375rem] !m-0 translate-y-[.5rem]"
            src={amazon}
            alt="Amazon logo"
          />
        </a>
      );
    },
  },
};

export const customRichTextComponent = (props: PortableTextComponents) => {
  return merge(cloneDeep(richTextComponent), props);
};
